import { useMemo } from 'react'
import { IntegrationServiceId } from '../../../../common/integrations'
import { AccountIntegrationConfigMappingType, getMappingTypeLabel } from '../../../../models/Account'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'
import { useGetMeQuery } from '../../../../store/user/user'
import { ContactAdminSettings } from './AdminSettingsTabs/InboundMappingSettings/ContactMappingSettings/ContactAdminSettings'
import { GeneralAdminSettings } from './AdminSettingsTabs/General/GeneralAdminSettings'
import { SessionAdminSettings } from './AdminSettingsTabs/OutboundMappingSettings/SessionMappingSettings/SessionAdminSettings'
import { useIsMappingTypeLocked } from './hooks'
import { IntegrationAdminSettingsCustom } from './IntegrationAdminSettingsCustom'

export const usePredefinedAdminSettings = (serviceId: IntegrationServiceId) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: userData } = useGetMeQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const hasIntegration = useMemo(() => {
    if (!userData || !userData.integrations) {
      return false
    }
    return !!userData.integrations.some((i) => i.serviceId === serviceId)
  }, [userData, serviceId])

  const isSessionLocked = useIsMappingTypeLocked(serviceId, AccountIntegrationConfigMappingType.SESSION)
  const isContactLocked = useIsMappingTypeLocked(serviceId, AccountIntegrationConfigMappingType.CONTACT)

  const predefinedMeetingSummaryAdminSettings = useMemo(
    () => [
      {
        id: 'meetingSummary',
        label: getMappingTypeLabel(AccountIntegrationConfigMappingType.SESSION),
        content: isSessionLocked
          ? () => <IntegrationAdminSettingsCustom serviceId={serviceId} mappingType={AccountIntegrationConfigMappingType.SESSION} />
          : () => <SessionAdminSettings serviceId={serviceId} disabled={!hasIntegration} />,
        disabled: !hasIntegration,
      },
    ],
    [hasIntegration, serviceId, isSessionLocked],
  )
  const predefinedContactAdminSettings = useMemo(
    () => [
      {
        id: 'contact',
        label: getMappingTypeLabel(AccountIntegrationConfigMappingType.CONTACT),
        content: isContactLocked
          ? () => <IntegrationAdminSettingsCustom serviceId={serviceId} mappingType={AccountIntegrationConfigMappingType.CONTACT} />
          : () => <ContactAdminSettings serviceId={serviceId} disabled={!hasIntegration} />,
        disabled: !hasIntegration,
      },
    ],
    [hasIntegration, serviceId, isContactLocked],
  )
  const predefinedGeneralAdminSettings = useMemo(
    () => [{ id: 'settings', label: 'Settings', content: () => <GeneralAdminSettings serviceId={serviceId} />, disabled: false }],
    [serviceId],
  )
  const predefinedAdminSettings = useMemo(
    () => [
      ...predefinedGeneralAdminSettings,
      ...(serviceId === IntegrationServiceId.ZOHO ? predefinedContactAdminSettings : []),
      ...predefinedMeetingSummaryAdminSettings,
    ],
    [serviceId, predefinedGeneralAdminSettings, predefinedContactAdminSettings, predefinedMeetingSummaryAdminSettings],
  )

  return predefinedAdminSettings
}
